// src/components/users/RolesPanel.js
// --- UPDATE (User Management) ------------------------------------------------------------
//   <TeamByRole>        who is who: Management · Supervision · Office & Helpdesk · Field (inspectors,
//                       technicians) · External contractors · Basic — every person with
//                       status, title and phone; click a person to open them
//   <PermissionsMatrix> what each role can do in every module (full / manage / edit / view / own),
//                       with the role's description and who it escalates to
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, SimpleGrid, HStack, VStack, Text, Badge, Avatar, Tooltip, Table, Thead, Tbody, Tr, Th, Td, Icon, useColorModeValue,
} from '@chakra-ui/react';
import { MdPhone } from 'react-icons/md';
import { ROLE_META, ROLE_OPTIONS, PERMISSION_MODULES, ROLE_PERMISSIONS, PERMISSION_LEVELS, roleMeta } from '../../config/roles';

export const TEAM_SECTIONS = [
    { key: 'management', label: 'Management', roles: ['admin', 'manager'], explain: 'Run FlowSense and the operation; manage users and approvals.' },
    { key: 'supervision', label: 'Supervisors', roles: ['supervisor'], explain: 'Dispatch crews, receive SLA escalations, approve AI reassignments.' },
    { key: 'office', label: 'Office & Helpdesk', roles: ['staff', 'helpdesk'], explain: 'Customer calls, tickets, scheduling, inventory and notifications.' },
    { key: 'field', label: 'Field teams', roles: ['inspector', 'tech'], explain: 'Inspectors and technicians working from the tech app.' },
    { key: 'external', label: 'Contractors', roles: ['contractor'], explain: 'External companies — only their assigned work.' },
    { key: 'basic', label: 'Basic accounts', roles: ['user'], explain: 'No operational access.' },
];
const STATUS_DOT = { active: 'green.400', on_leave: 'orange.400', inactive: 'gray.400' };

export const TeamByRole = memo(({ users = [], onOpenUser, onFilterRole }) => {
    const card = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const hover = useColorModeValue('gray.50', 'whiteAlpha.100');
    const byRole = useMemo(() => {
        const m = {};
        users.forEach((u) => { (m[u.role] = m[u.role] || []).push(u); });
        Object.values(m).forEach((l) => l.sort((a, b) => (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1) || String(a.display_name).localeCompare(String(b.display_name))));
        return m;
    }, [users]);
    return (
        <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={4}>
            {TEAM_SECTIONS.map((s) => {
                const people = s.roles.flatMap((r) => byRole[r] || []);
                return (
                    <Box key={s.key} bg={card} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
                        <HStack justify="space-between" mb={1}>
                            <Text fontWeight="800">{s.label}</Text>
                            <Badge borderRadius="full">{people.filter((u) => u.status === 'active').length} active</Badge>
                        </HStack>
                        <Text fontSize="xs" color={muted} mb={3}>{s.explain}</Text>
                        {s.roles.map((r) => {
                            const list = byRole[r] || [];
                            const meta = ROLE_META[r];
                            return (
                                <Box key={r} mb={3}>
                                    <HStack spacing={2} mb={1} as="button" type="button" onClick={() => onFilterRole?.(r)} aria-label={`Show ${meta.label} accounts`}>
                                        <Badge colorScheme={meta.color}>{meta.label}</Badge>
                                        <Text fontSize="xs" color={muted}>{list.length}</Text>
                                    </HStack>
                                    {list.length === 0 ? <Text fontSize="xs" color={muted} pl={1}>None yet</Text> : (
                                        <VStack align="stretch" spacing={0.5}>
                                            {list.slice(0, 12).map((u) => (
                                                <HStack key={u.id} as="button" type="button" onClick={() => onOpenUser?.(u)} p={1.5} borderRadius="md" _hover={{ bg: hover }} spacing={2} textAlign="left" aria-label={`Open ${u.display_name}`}>
                                                    <Box position="relative"><Avatar size="xs" name={u.display_name} bg={`${meta.color}.400`} color="white" /><Box position="absolute" bottom={0} right={0} w={2} h={2} borderRadius="full" bg={STATUS_DOT[u.status] || 'gray.400'} borderWidth="1px" borderColor={card} /></Box>
                                                    <Box flex={1} minW={0}>
                                                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>{u.display_name}</Text>
                                                        <Text fontSize="2xs" color={muted} noOfLines={1}>{[u.job_title, u.department].filter(Boolean).join(' · ') || `@${u.username}`}{u.status !== 'active' ? ` · ${u.status.replace('_', ' ')}` : ''}</Text>
                                                    </Box>
                                                    {u.phone && <Tooltip label={u.phone}><Box><Icon as={MdPhone} color={muted} boxSize={3.5} /></Box></Tooltip>}
                                                </HStack>
                                            ))}
                                            {list.length > 12 && <Text fontSize="2xs" color="purple.400" as="button" type="button" onClick={() => onFilterRole?.(r)} textAlign="left" pl={1.5}>+ {list.length - 12} more — show all</Text>}
                                        </VStack>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}
        </SimpleGrid>
    );
});
TeamByRole.displayName = 'TeamByRole';
TeamByRole.propTypes = { users: PropTypes.array, onOpenUser: PropTypes.func, onFilterRole: PropTypes.func };

export const PermissionsMatrix = memo(({ catalog }) => {
    const border = useColorModeValue('gray.200', 'gray.700');
    const head = useColorModeValue('gray.50', 'gray.900');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const counts = Object.fromEntries((catalog?.roles || []).map((r) => [r.id, r.counts]));
    const escal = Object.fromEntries((catalog?.roles || []).map((r) => [r.id, r.escalates_to]));
    return (
        <Box overflowX="auto" borderWidth="1px" borderColor={border} borderRadius="xl">
            <Table size="sm">
                <Thead bg={head}>
                    <Tr>
                        <Th minW="220px">Role</Th>
                        {PERMISSION_MODULES.map((m) => <Th key={m.key} textAlign="center" fontSize="2xs" whiteSpace="normal" minW="80px">{m.label}</Th>)}
                    </Tr>
                </Thead>
                <Tbody>
                    {ROLE_OPTIONS.map((r) => (
                        <Tr key={r.id}>
                            <Td>
                                <HStack spacing={2}><Badge colorScheme={r.color}>{r.label}</Badge>{counts[r.id] && <Text fontSize="2xs" color={muted}>{counts[r.id].active}/{counts[r.id].total}</Text>}</HStack>
                                <Text fontSize="2xs" color={muted} mt={1} maxW="260px" whiteSpace="normal">{r.description}</Text>
                                {(escal[r.id] || []).length > 0 && <Text fontSize="2xs" color={muted}>Escalates to: {escal[r.id].map((x) => roleMeta(x).short).join(' → ')}</Text>}
                            </Td>
                            {PERMISSION_MODULES.map((m) => {
                                const lv = ROLE_PERMISSIONS[r.id]?.[m.key] || '—';
                                const info = PERMISSION_LEVELS[lv] || PERMISSION_LEVELS['—'];
                                return (
                                    <Td key={m.key} textAlign="center">
                                        {lv === '—' ? <Text color={muted}>—</Text> : <Tooltip hasArrow label={`${r.label} · ${m.label}: ${info.explain}`}><Badge colorScheme={info.color} variant={lv === 'full' ? 'solid' : 'subtle'} cursor="help">{info.label}</Badge></Tooltip>}
                                    </Td>
                                );
                            })}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );
});
PermissionsMatrix.displayName = 'PermissionsMatrix';
PermissionsMatrix.propTypes = { catalog: PropTypes.object };
